import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import FileSaver from 'file-saver';
import moment from 'moment';
import { ConfirmationService, MessageService } from 'primeng/api';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteModule,
} from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Table, TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { Club } from '../../../model/Club';
import { HttpResponse } from '../../../model/HttpResponse';
import { AcademyAdministratorOperation } from '../../../model/administrator/AcademyAdministratorOperation';
import { AcademyAdministratorType } from '../../../model/administrator/AcademyAdministratorType';
import { AdministratorEntity } from '../../../model/administrator/AdministratorEntity';
import { Member } from '../../../model/member';
import { ClubService } from '../../../services/club.service';
import { GestionService } from '../../../services/gestion.service';
import { LoadingService } from '../../../services/loading.service';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';
import { CrudTableComponent } from '../../shared/tables/crud-table/crud-table.component';
import { MemberInformationComponent } from "../matriculas/member-information/member-information.component";

type ClubOption = Club & { displayName: string };

@Component({
    selector: 'app-clubes',
    standalone: true,
    providers: [MessageService, ConfirmationService],
    templateUrl: './clubes.component.html',
    styleUrl: './clubes.component.scss',
    imports: [
        AutoCompleteModule,
        FormsModule,
        PageHeaderComponent,
        CrudTableComponent,
        TableModule,
        CommonModule,
        SelectButtonModule,
        InputTextModule,
        MultiSelectModule,
        ButtonModule,
        DialogModule,
        CheckboxModule,
        ToastModule,
        MemberInformationComponent
    ]
})
export class ClubesComponent implements OnInit {
  clubes: ClubOption[] = [];
  clubSelected!: ClubOption;
  suggestions: any[] = [];
  members: Member[] = [];
  showMembersTable: boolean = false;
  memeberSelected!: Member | null;
  showMemberDetail: boolean = false;
  showClubInfo: boolean = false;
  isEditingClub: boolean = false;
  clubBeingEdited: Club | null = null;
  constructor(
    private clubService: ClubService,
    private loadingService: LoadingService,
    private gestionService:GestionService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.getClubes();
  }

  getClubes() {
    this.loadingService.showLoading();
    this.clubService.getAllClubs().subscribe((res: HttpResponse) => {
      // El sufijo solo se muestra; no debe alterar club.name porque ese valor se envía al actualizar
      this.clubes = res.response.map((club: Club) => ({
        ...club,
        displayName: this.buildDisplayName(club),
      }));
      this.loadingService.hideLoading();
    });
  }

  private buildDisplayName(club: Club): string {
    return club.administrativeInformation?.isExternal
      ? `${club.name} (No Vinculado)`
      : club.name;
  }

  searchClubes(event: AutoCompleteCompleteEvent) {
    this.suggestions = this.clubes.map((club) => event.query + '-' + club.name);
  }

  filterClub(event: AutoCompleteCompleteEvent) {
    let filtered: any[] = [];
    let query = event.query;

    for (let i = 0; i < (this.clubes as any[]).length; i++) {
      let club = (this.clubes as any[])[i];
      if (club.displayName.toLowerCase().indexOf(query.toLowerCase()) >= 0) {
        filtered.push(club);
      }
    }

    this.suggestions = filtered;
  }

  async onSelectClub() {
    this.loadingService.showLoading();
    let entity: AdministratorEntity = {
      operation: AcademyAdministratorOperation.SELECT,
      payload: {
        options: {
          conditionals: [
            {
              conditionalConnector: null,
              logicOperator: '=',
              evaluatedProperty: 'club.id',
              parameterName: '@Option1',
              parameterValue:this.clubSelected.id,
            }
          ],
        },
      },
      type: AcademyAdministratorType.MEMBERS,
    };
    this.gestionService.sendRequest(entity).subscribe((res: HttpResponse) => {
      if (res.response.length > 0) {
        this.members = res.response;
        this.showMembersTable = true;
      } else {
        this.showMembersTable = false;
      }
      this.loadingService.hideLoading();
    });
  }

  clear(table: Table) {
    table.clear();
  }

  calculateYearsOld(birthDay: string) {
    return moment().diff(birthDay, 'years');
  }

  getMemberGenere(genereShort: string): string {
    switch (genereShort) {
      case 'NB':
        return 'No Binario';
      case 'M':
        return 'Masculino';
      case 'F':
        return 'Femenino';
      default:
        return 'No Especifica';
    }
  }
  exportExcel() {
    import('xlsx').then((xlsx) => {
      const dataToPrint = this.members.map((member:Member) => {
        const memberDetailed = {
          club:member.club.name,
          modalidad:member.modalidad,
          rol:member.rol,
          tipoIdentificacion:member.idType,
          numeroIdentificacion:member.idNumber,
          nombre:member.name,
          apellido:member.lastName,
          genero:this.getMemberGenere(member.gender),
          edad:this.calculateYearsOld(member.birthDay),
          email:member.contactInformation.email,
          telefono:member.contactInformation.mobile,
          fechaVinculacion:member.auditData.createdDate,
          estado: member.isActive ? 'Activo' : 'Inactivo',
          
        }

        return memberDetailed;
      })
        const worksheet = xlsx.utils.json_to_sheet(dataToPrint);
        const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
        const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
        this.saveAsExcelFile(excelBuffer, this.clubSelected.name);
    });

    
}

saveAsExcelFile(buffer: any, fileName: string): void {
  let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  let EXCEL_EXTENSION = '.xlsx';
  const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
  });
  FileSaver.saveAs(data, fileName + EXCEL_EXTENSION);
}
editMember(member:Member){
  this.memeberSelected = member;
  this.showMemberDetail = true;
}

closeDialog(){
  this.showMemberDetail = false;
  this.memeberSelected = null;
}

updateMember(member:Member){
  this.closeDialog();
  this.onSelectClub();
}

editClub(){
  this.isEditingClub = true;
  // Copia profunda para que Cancelar no deje cambios en contactInformation/administrativeInformation
  this.clubBeingEdited = structuredClone(this.clubSelected);
}

cancelEditClub(){
  this.isEditingClub = false;
  this.clubBeingEdited = null;
}

saveClub(){
  if(!this.clubBeingEdited) return;
  this.clubBeingEdited.name = this.clubBeingEdited.name?.trim();

  if(!this.validateClubData(this.clubBeingEdited)){
    return;
  }
  
  this.loadingService.showLoading();
  const clubToUpdate:Club ={
  id: this.clubBeingEdited.id,
  name: this.clubBeingEdited.name,
  clubId: this.clubBeingEdited.clubId,
  modalities: this.clubBeingEdited.modalities,
  isActive: this.clubBeingEdited.isActive,
  contactInformation: this.clubBeingEdited.contactInformation,
  administrativeInformation: this.clubBeingEdited.administrativeInformation
  }
  this.clubService.updateClub(clubToUpdate).subscribe({
    next: (res: HttpResponse) => {
      this.loadingService.hideLoading();
      if(res.response){
        this.clubSelected = {
          ...this.clubBeingEdited!,
          displayName: this.buildDisplayName(this.clubBeingEdited!),
        };
        this.getClubes();
        // Los members guardan una copia del club; se recargan para reflejar el nombre actualizado
        this.onSelectClub();
        this.isEditingClub = false;
        this.clubBeingEdited = null;
        this.notifyClubUpdated(res.response);
      } else {
        this.messageService.add({
          severity: 'error', 
          summary: 'Error', 
          detail: 'No se pudo actualizar el club'
        });
      }
    },
    error: (error) => {
      this.loadingService.hideLoading();
      this.messageService.add({
        severity: 'error', 
        summary: 'Error', 
        detail: error?.status === 422 && error.error?.message
          ? error.error.message
          : 'Error al actualizar el club'
      });
      console.error('Error updating club:', error);
    }
  });
}

private notifyClubUpdated(result: any) {
  if(result?.membersFailed > 0){
    this.messageService.add({
      severity: 'warn',
      summary: 'Actualización parcial',
      detail: `El club se actualizó, pero ${result.membersFailed} vinculaciones no quedaron con el nuevo nombre. Guarde nuevamente para reintentar.`,
      life: 10000
    });
    return;
  }
  this.messageService.add({
    severity: 'success',
    summary: 'Éxito',
    detail: result?.membersUpdated > 0
      ? `Club actualizado correctamente. Se actualizaron ${result.membersUpdated} vinculaciones.`
      : 'Club actualizado correctamente'
  });
}

private validateClubData(club: Club): boolean {
  if(!club.name){
    this.messageService.add({
      severity: 'warn',
      summary: 'Validación',
      detail: 'El nombre del club es obligatorio'
    });
    return false;
  }

  const nameInUse = this.clubes.some(
    (other) => other.id !== club.id && other.name?.trim().toLowerCase() === club.name.toLowerCase()
  );
  if(nameInUse){
    this.messageService.add({
      severity: 'warn',
      summary: 'Validación',
      detail: 'Ya existe un club con ese nombre'
    });
    return false;
  }

  if(!club.contactInformation.email || !this.isValidEmail(club.contactInformation.email)){
    this.messageService.add({
      severity: 'warn', 
      summary: 'Validación', 
      detail: 'Por favor ingrese un email válido'
    });
    return false;
  }
  
  return true;
}

private isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

handleMemberDeletion(member: Member) {
  this.loadingService.showLoading();
  let entity: AdministratorEntity = {
    operation: AcademyAdministratorOperation.DELETE,
    payload: {
      id: member.id
    },
    type: AcademyAdministratorType.MEMBERS,
  };
  this.gestionService.sendRequest(entity).subscribe((res: HttpResponse) => {
    if(res.response){
      this.messageService.add({severity:'success', summary:'Éxito', detail: 'Miembro eliminado correctamente'});
      this.closeDialog();
      this.onSelectClub();
    }
    this.loadingService.hideLoading();
  });
}

}
